import { useState } from 'react';
import { useCreateEndpointTestSuite } from '@/hooks/useEndpointTestEvals';
import { useToast } from '@/contexts/ToastContext';

interface SuiteToolbarControlsProps {
  endpointId: string;
  selectedIds: string[];
}

/**
 * The run toolbar's suite control (task 1180): "Save as suite" names the
 * current selection — how a suite is created. Membership is edited in the
 * Suites block's dialog, not through the selection (task 1209).
 */
export function SuiteToolbarControls({ endpointId, selectedIds }: SuiteToolbarControlsProps) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const createSuite = useCreateEndpointTestSuite();
  const toast = useToast();
  const button =
    'px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50';

  if (!naming) {
    return (
      <button type="button" onClick={() => setNaming(true)} className={button}>
        Save as suite
      </button>
    );
  }

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createSuite.mutateAsync({ endpointId, name: trimmed, testIds: selectedIds });
      toast.success('Suite saved', trimmed);
      setNaming(false);
      setName('');
    } catch (err) {
      toast.error('Failed to save suite', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        aria-label="Suite name"
        value={name}
        autoFocus
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') setNaming(false);
        }}
        placeholder="Suite name"
        className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-900 dark:text-white"
      />
      <button type="button" onClick={() => void save()} disabled={!name.trim() || createSuite.isPending} className={button}>
        Save suite
      </button>
    </span>
  );
}

export default SuiteToolbarControls;
